import { IconArrowBackUp, IconTrash } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { useState } from 'react';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { NSButton, NSCard } from 'bricks';
import NSUserAvatar from 'bricks/NSUserAvatar/NSUserAvatar';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import useToast from 'hooks/useToast';
import { formatUTCtoLocal } from 'ns_libs/formatter';
import { TimelineComment } from 'views/Tasks/Timeline/types';
import { useDeleteComment } from 'views/Tasks/Timeline/hooks/useDeleteComment';
import UserDisplay from 'components/UserDisplay/UserDisplay';
import { resolveUserDisplayName } from 'components/UserDisplay/helpers';
import { useOrgUser } from 'components/UserDisplay/hooks/useOrgUser';
import './CommentItem.scss';

export interface CommentItemProps {
    comment: TimelineComment;
    orgId: string;
    dealId: number;
    timelineId: number;
    currentUserCorrelationId: string | null;
    onReply: () => void;
}

const CommentItem = ({
    comment, orgId, dealId, timelineId, currentUserCorrelationId, onReply,
}: CommentItemProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const { showError } = useToast();
    const { mutate: deleteComment } = useDeleteComment();
    const { user: author, isLoading } = useOrgUser(comment.authorUserCorrelationId);

    const anchorId = `comment-${comment.id}`;

    if (comment.deletedAt !== null) {
        return (
            <div id={anchorId} className="d-flex align-items-center p-2 my-2 text-muted font-italic font-13">
                <NSIcon icon={IconTrash} className="mr-2" />
                This comment was deleted
                <span className="ml-2 font-12">{formatUTCtoLocal(comment.deletedAt)}</span>
            </div>
        );
    }

    const authorName = resolveUserDisplayName(author, isLoading);
    const canDelete = currentUserCorrelationId !== null
        && comment.authorUserCorrelationId === currentUserCorrelationId;

    const handleConfirmDelete = () => {
        deleteComment(
            {
                orgId, dealId, timelineId, commentId: comment.id,
            },
            { onError: () => showError('Failed to delete comment. Please try again.') },
        );
        setIsConfirmOpen(false);
    };

    return (
        <NSCard
            id={anchorId}
            className="CommentItem NSCard--level-3 d-flex flex-row align-items-start p-2 my-2"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <NSUserAvatar
                size={36}
                radius={18}
                className="mr-2"
                userName={authorName}
                userPicture={author?.picture}
            />
            <div className="flex-grow-1">
                <div className="d-flex align-items-baseline">
                    <UserDisplay
                        correlationId={comment.authorUserCorrelationId}
                        className="text-white font-weight-semibold"
                    />
                    <span className="text-muted font-12 ml-2">{formatUTCtoLocal(comment.createdAt)}</span>
                </div>
                <div className="CommentItem__body text-white">
                    {parse(DOMPurify.sanitize(comment.bodyHtml))}
                </div>
            </div>

            {isHovered && (
                <div className="d-flex align-items-center align-self-center">
                    <NSButton
                        color="link"
                        className="text-muted hover:text-white p-0 px-2"
                        icon={IconArrowBackUp}
                        ariaLabel="Reply"
                        callback={onReply}
                    />
                    {canDelete && (
                        <NSButton
                            color="link"
                            className="text-muted hover:text-danger p-0 px-2"
                            icon={IconTrash}
                            ariaLabel="Delete comment"
                            callback={() => setIsConfirmOpen(true)}
                        />
                    )}
                </div>
            )}

            <ConfirmationModal
                isOpen={isConfirmOpen}
                danger
                toggle={() => setIsConfirmOpen(false)}
                onCancel={() => setIsConfirmOpen(false)}
                onConfirm={handleConfirmDelete}
                modalHeaderText="Delete comment?"
                modalFooterButtonText="Delete"
                modalFooterButtonColor="danger"
            >
                <p>Are you sure you want to delete this comment? This action cannot be undone.</p>
            </ConfirmationModal>
        </NSCard>
    );
};

export default CommentItem;
